import * as React from 'react';
import { Button } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { type Direction, initiativeReport } from '../../../../../shared/riskregister/productReport';
import type { Payload, Row, ViewState } from '../../../../../shared/riskregister/types';
import '../../productReport.css';

const VISIBLE_LIMIT = 10;

const DIRECTION_LABEL: Record<Direction, string> = {
  improving: 'Improving',
  worsening: 'Getting worse',
  steady: 'About the same',
};

export const ProductInitiativeTable: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
  state: ViewState;
  onChange: (change: Partial<ViewState>) => void;
}> = ({ payload, rows, state, onChange }) => {
  const [showAll, setShowAll] = React.useState(false);
  const initiatives = React.useMemo(() => initiativeReport(rows, payload), [payload, rows]);
  const scope = state.area === 'ALL' ? 'all platform areas' : state.area;

  if (!initiatives.length) {
    return <p className="risk-register__empty">There are no initiatives to report on for {scope} right now.</p>;
  }

  const visible = showAll ? initiatives : initiatives.slice(0, VISIBLE_LIMIT);

  return (
    <>
      <div className="risk-register__table-toolbar">
        <span>
          {initiatives.length} initiative{initiatives.length === 1 ? '' : 's'} across {scope}, worst first. Pick one to
          narrow the whole page down to it.
        </span>
        {state.unit && (
          <Button
            variant="link"
            isInline
            onClick={() => onChange({ unit: '' })}
            aria-label="Show every initiative again"
          >
            × Clear
          </Button>
        )}
      </div>
      <div className="risk-register__table-wrap">
        <Table aria-label="How each initiative is doing" variant="compact">
          <Thead>
            <Tr>
              <Th scope="col">Initiative</Th>
              <Th scope="col">Open</Th>
              <Th scope="col">Late</Th>
              <Th scope="col">Highest severity</Th>
              <Th scope="col">Finished</Th>
              <Th scope="col">Direction</Th>
            </Tr>
          </Thead>
          <Tbody>
            {visible.map((entry) => (
              <Tr key={entry.id}>
                <Td dataLabel="Initiative">
                  <Button
                    variant="link"
                    isInline
                    onClick={() => onChange({ acct: 'epic', unit: `epic:${entry.unit}` })}
                    aria-label={`Narrow the whole page down to ${entry.name}`}
                  >
                    {entry.name}
                  </Button>
                </Td>
                <Td dataLabel="Open">{entry.open}</Td>
                <Td dataLabel="Late">{entry.late}</Td>
                <Td dataLabel="Highest severity">{entry.severe}</Td>
                <Td dataLabel="Finished">{entry.finished}</Td>
                <Td dataLabel="Direction">
                  <span
                    role="img"
                    aria-label={`${DIRECTION_LABEL[entry.direction]}: open work went from ${entry.previousOpen} last month to ${entry.latestOpen} this month.`}
                    className={`risk-register__product-direction risk-register__product-direction--${entry.direction}`}
                  >
                    <span aria-hidden="true">{DIRECTION_LABEL[entry.direction]}</span>
                  </span>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
      {initiatives.length > VISIBLE_LIMIT && (
        <Button variant="link" isInline onClick={() => setShowAll((current) => !current)}>
          {showAll ? `Show top ${VISIBLE_LIMIT}` : `Show all ${initiatives.length} initiatives`}
        </Button>
      )}
      <p className="risk-register__note">
        Late means the work has passed the date it was promised for. Highest severity means the risk review rated it
        most serious. Direction compares how much work was open last month against this month.
      </p>
    </>
  );
};
